import React from "react";

import classnames from "classnames";
import _ from "lodash";
import PropTypes from "prop-types";
import { filterPlan } from "QuotePage/PlanFilters";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { Option } from "Cobalt/Dropdown";
import { Popover } from "Cobalt/Popover";
import BodyText from "Common/BodyText";
import SICheckbox from "Common/SICheckbox";
import SIOverlayTrigger from "Common/SIOverlayTrigger";

import makeBem from "Utils/makeBem";

const bem = makeBem("MultiSelectFilter");

export default class MultiSelectFilter extends React.Component {
  render() {
    const { allPlans, filterConfig, options, filterKey, onChangeFilter, disableFilterCountIndicator, defaultOptions } = this.props;

    const listOptions = options.map((option) => option.value);
    const filterType = this.isMinMaxFilter(listOptions) ? "ranges" : "include";

    const currentFilter = _.get(filterConfig, filterKey);
    const selectedOptions = (currentFilter ? currentFilter[filterType] : null) || defaultOptions;

    // NOTE: behavior here is broken for prescriptions on MD/tablet displays

    return (
      <ul className="p-0 list-none si-menu">
        {options.map((option, key) => {
          // Disable filter if clicking it would result in a zero plan state
          const checked = _.find(selectedOptions, (val) => _.isEqual(val, option.value));

          const filterCount = this.getFilterCount(option, filterKey, filterConfig, allPlans);

          const filterDisabled = !checked && filterCount === 0;

          return (
            <Option
              key={key}
              value={option.value}
              onClick={() => this.handleSelect(option.value, filterDisabled, onChangeFilter, options, filterConfig, filterKey, option.label)}
              className={classnames("p-4 sm:p-1 items-center justify-between", { "selected-filter-item": checked })}
              disabled={filterDisabled}
              keepOpenOnSelect
            >
              {this.renderMenuText(option, checked, filterCount, filterDisabled, disableFilterCountIndicator)}
            </Option>
          );
        })}
      </ul>
    );
  }

  getFilterCount(option, filterKey, filterConfig, allPlans) {
    const filter = {};

    if (this.isMinMaxFilter([option.value])) {
      _.set(filter, filterKey, option.value);
    } else {
      _.set(filter, filterKey, { include: [option.value] });
    }

    const newFilters = _.extend({}, filterConfig, filter);
    return _.filter(allPlans, _.partial(filterPlan, newFilters)).length;
  }

  renderMenuText(option, checked, filterCount, filterDisabled, disableFilterCountIndicator) {
    let filterCountIndicator = null;

    if (!disableFilterCountIndicator) {
      filterCountIndicator = (
        <BodyText muted className="absolute right-5 sm:hidden">
          &nbsp;({filterCount})
        </BodyText>
      );
    }

    const checkbox = <SICheckbox disabled={filterDisabled} checked={checked} label={option.label} onChange={() => {}} className="flex-1" />;

    if (option.tooltip) {
      const popover = (
        <Popover showBackdrop={false} className="filter-tooltip" id={`${option.value}_filter`}>
          <span dangerouslySetInnerHTML={{ __html: option.tooltip }} />
        </Popover>
      );

      return (
        <div className="flex items-center justify-between">
          <SIOverlayTrigger trackType="filter" trackId={option.label} overlay={popover}>
            {checkbox}
          </SIOverlayTrigger>
          {filterCountIndicator}
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between">
        {checkbox}
        {filterCountIndicator}
      </div>
    );
  }

  handleSelect(value, filterDisabled, onChange, options, filterConfig, filterKey, label) {
    if (filterDisabled) {
      return;
    }
    const listOptions = options.map((option) => option.value);
    const currentFilter = _.get(filterConfig, filterKey);
    const filterType = this.isMinMaxFilter(listOptions) ? "ranges" : "include";
    const currentOptions = (currentFilter ? currentFilter[filterType] : null) || [];
    let newFilter = {};

    let actionPerformed = "filter_added";
    // Add or remove the selected option from the new filter
    if (_.find(currentOptions, (val) => _.isEqual(val, value))) {
      newFilter[filterType] = _.reject(currentOptions, (val) => _.isEqual(val, value));

      actionPerformed = "filter_removed";
    } else {
      newFilter[filterType] = currentOptions.concat(value);
    }

    // When all filters within a category are deselected, clear the filter
    if (newFilter[filterType].length === 0) {
      newFilter = {};
    }

    AnalyticsTracking.trackEvent(actionPerformed, {
      location: "dropdown_filters",
      filter_category: this.props.menuTitle,
      changed_filter: label,
    });

    onChange(filterKey, newFilter);
  }

  isMinMaxFilter(listOptions) {
    return listOptions.length > 0 && (Object.keys(listOptions[0]).includes("max") || Object.keys(listOptions[0]).includes("min"));
  }
}

MultiSelectFilter.propTypes = {
  allPlans: PropTypes.array.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
  filterConfig: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  filterKey: PropTypes.string.isRequired,
};

MultiSelectFilter.defaultProps = {
  className: "suite-selection-dropdown",
};
